import { useEffect, useRef } from "react";
import * as d3 from "d3";
import { apiFetch } from "../../api/apiClient";

const HEIGHT = 520;

const margin = {
  top: 20,
  right: 20,
  bottom: 70,
  left: 90,
};

const MIN_ZOOM = 1;
const MAX_ZOOM = 20;
// Multiplicative step used by the keyboard +/- zoom controls.
const ZOOM_STEP = 1.5;

function normalizeScatterRow(d) {
  const x = Number(
    d.generation ??
      d.x ??
      d["Solar Output, MWdc"] ??
      d.solarOutput ??
      d.mwdc
  );
  const y = Number(
    d.irradiance ??
      d.y ??
      d["POA Solar Irradiance, W/m²"] ??
      d.poaIrradiance
  );
  if (!Number.isFinite(x) || !Number.isFinite(y)) {
    return null;
  }
  return { x, y };
}

// Default must be a stable reference. `data = []` creates a new array every
// render, which retriggers the fetch effect (e.g. on tooltip hover).
const EMPTY_DATA = [];
const DEFAULT_X_DOMAIN_MIN = 0;
const DEFAULT_X_DOMAIN_MAX = 1000;
const DEFAULT_Y_DOMAIN_MIN = 0;
const DEFAULT_Y_DOMAIN_MAX = 1200;

export default function SolarGenerationVsIrradiance({
  portfolioName,
  duration = "june-2026",
  /** Optional inclusive month range (YYYY-MM-01). When set, backend samples across the span. */
  startMonth = null,
  endMonth = null,
  minIrradiance = null,
  minMwPerIrradiance = null,
  xDomainMin = DEFAULT_X_DOMAIN_MIN,
  xDomainMax = DEFAULT_X_DOMAIN_MAX,
  yDomainMin = DEFAULT_Y_DOMAIN_MIN,
  yDomainMax = DEFAULT_Y_DOMAIN_MAX,
  data = EMPTY_DATA,
  onTooltip,
  xAxisLabel = "Solar Output, MWdc",
  yAxisLabel = "POA Solar Irradiance, W/m²",
  ariaLabel = "Solar generation vs irradiance scatter plot. Tab or click to select a data point; use arrow keys to move the selection; use + and - to zoom in and out around the selected point.",
}) {
  const containerRef = useRef(null);
  const svgRef = useRef(null);
  const dataRef = useRef(null);
  // Persist the current zoom transform across resizes / data updates so the
  // user's view is preserved when the chart re-renders.
  const transformRef = useRef(d3.zoomIdentity);
  // Persistent click/keyboard selection — kept across draws so the user's
  // pick survives resizes, focus/blur, and data refreshes.
  const selectedRef = useRef(null);
  // Latest tooltip setter, read at hover/keyboard time so the draw effect
  // doesn't tear down just because the parent passed a new callback identity.
  const onTooltipRef = useRef(onTooltip);
  onTooltipRef.current = onTooltip;
  const drawRef = useRef(() => {});

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (Array.isArray(data) && data.length > 0) {
        dataRef.current = data.map(normalizeScatterRow).filter(Boolean);
        drawRef.current();
        return;
      }

      if (!portfolioName) {
        dataRef.current = [];
        drawRef.current();
        return;
      }

      try {
        const params = new URLSearchParams();
        if (startMonth) params.set("startMonth", startMonth);
        if (endMonth) params.set("endMonth", endMonth);
        if (minIrradiance != null) params.set("minIrradiance", String(minIrradiance));
        if (minMwPerIrradiance != null) params.set("minMwPerIrradiance", String(minMwPerIrradiance));
        const qs = params.toString();
        const path =
          `/api/graph/portfolio/${portfolioName}/insights/${encodeURIComponent(duration)}/solar-generation-vs-solar-irradiance` +
          (qs ? `?${qs}` : "");
        const payload = await apiFetch(path);
        if (cancelled) return;
        const rows = Array.isArray(payload?.results)
          ? payload.results
          : Array.isArray(payload)
            ? payload
            : [];
        dataRef.current = rows.map(normalizeScatterRow).filter(Boolean);
        drawRef.current();
      } catch (error) {
        console.error("Failed to load solar generation vs irradiance:", error);
        if (cancelled) return;
        dataRef.current = [];
        drawRef.current();
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [portfolioName, duration, startMonth, endMonth, minIrradiance, minMwPerIrradiance, data]);

  useEffect(() => {
    if (!containerRef.current) return;

    /* =========================
       TOOLTIP fallback (only when page-level ChartTooltip isn't used)
       ========================= */
    const tooltip = onTooltipRef.current
      ? null
      : d3
          .select("body")
          .append("div")
          .style("position", "absolute")
          .style("left", "0")
          .style("top", "0")
          .style("background", "white")
          .style("border", "1px solid #ccc")
          .style("padding", "6px")
          .style("border-radius", "4px")
          .style("pointer-events", "none")
          .style("box-shadow", "0 0 10px rgba(0,0,0,0.25)")
          .style("opacity", 0);

    const format = d3.format(",.1f");

    // Bridge ref so the effect-scope keyboard/focus listeners (which
    // outlive a single draw) can always drive the most recent chart
    // instance. Populated at the end of every `draw()` call.
    const handlersRef = { current: null };

    function draw() {
      if (!containerRef.current || !dataRef.current?.length) return;

      const width = containerRef.current.clientWidth;
      const innerWidth = width - margin.left - margin.right;
      const innerHeight = HEIGHT - margin.top - margin.bottom;

      const svg = d3.select(svgRef.current);
      svg.selectAll("*").remove();

      svg
        .append("defs")
        .append("clipPath")
        .attr("id", "chart-clip")
        .append("rect")
        .attr("width", innerWidth)
        .attr("height", innerHeight);

      const g = svg
        .append("g")
        .attr("transform", `translate(${margin.left},${margin.top})`);

      /* =========================
         BASE SCALES
         (xBase / yBase never change — zoom rescales them per-render.)
         ========================= */
      const xBase = d3
        .scaleLinear()
        .domain([xDomainMin, xDomainMax])
        .range([0, innerWidth]);

      const yBase = d3
        .scaleLinear()
        .domain([yDomainMin, yDomainMax])
        .range([innerHeight, 0]);

      /* =========================
         LAYER GROUPS
         ========================= */
      const gridYG = g.append("g").attr("class", "grid grid-y");
      const gridXG = g
        .append("g")
        .attr("class", "grid grid-x")
        .attr("transform", `translate(0,${innerHeight})`);

      const axisYG = g.append("g").attr("class", "axis axis-y");
      const axisXG = g
        .append("g")
        .attr("class", "axis axis-x")
        .attr("transform", `translate(0,${innerHeight})`);

      const plotArea = g
        .append("g")
        .attr("class", "plot-area")
        .attr("clip-path", "url(#chart-clip)");

      const dotsG = plotArea.append("g").attr("class", "dots");
      const trendG = plotArea.append("g").attr("class", "trend");
      // Active hover dot lives inside the clipped plot area so it can't
      // visually escape the chart when zoomed in.
      const activeDot = plotArea
        .append("circle")
        .attr("class", "active-dot")
        .attr("r", 9)
        .attr("fill", "#4472C4")
        .attr("stroke", "#fff")
        .attr("stroke-width", 1.5)
        .style("pointer-events", "none")
        .style("opacity", 0);

      const crosshairV = g
        .append("line")
        .attr("stroke", "#666")
        .attr("y1", 0)
        .attr("y2", innerHeight)
        .style("pointer-events", "none")
        .style("opacity", 0);

      const crosshairH = g
        .append("line")
        .attr("stroke", "#666")
        .attr("x1", 0)
        .attr("x2", innerWidth)
        .style("pointer-events", "none")
        .style("opacity", 0);

      /* =========================
         AXIS TITLES
         ========================= */
      g.append("text")
        .attr("transform", "rotate(-90)")
        .attr("x", -innerHeight / 2)
        .attr("y", -70)
        .attr("text-anchor", "middle")
        .style("font-size", "14px")
        .style("font-weight", "bold")
        .text(yAxisLabel);

      g.append("text")
        .attr("x", innerWidth / 2)
        .attr("y", innerHeight + 50)
        .attr("text-anchor", "middle")
        .style("font-size", "14px")
        .style("font-weight", "bold")
        .text(xAxisLabel);

      /* =========================
         INTERACTION OVERLAY
         (must be the topmost element so it captures pointer + wheel events)
         ========================= */
      const overlay = g
        .append("rect")
        .attr("class", "interaction-overlay")
        .attr("width", innerWidth)
        .attr("height", innerHeight)
        .attr("fill", "transparent")
        .style("cursor", "crosshair");

      /* =========================
         REGRESSION (computed once — line is rescaled per-zoom)
         ========================= */
      let regressionData = null;
      if (dataRef.current.length >= 2) {
        const xs = dataRef.current.map((d) => d.x);
        const ys = dataRef.current.map((d) => d.y);
        const meanX = d3.mean(xs);
        const meanY = d3.mean(ys);
        const denom = d3.sum(xs.map((v) => (v - meanX) ** 2));
        const slope =
          denom !== 0
            ? d3.sum(xs.map((xv, i) => (xv - meanX) * (ys[i] - meanY))) / denom
            : 0;
        const intercept = meanY - slope * meanX;
        regressionData = [
          { x: xDomainMin, y: slope * xDomainMin + intercept },
          { x: xDomainMax, y: slope * xDomainMax + intercept },
        ];
      }

      const DOT_R = 4;
      const SNAP_RADIUS = 12;

      let xScale = xBase;
      let yScale = yBase;
      let quadtree = null;

      function applyTransform(transform) {
        transformRef.current = transform;
        xScale = transform.rescaleX(xBase);
        yScale = transform.rescaleY(yBase);

        const [xMin, xMax] = xScale.domain();
        const [yMin, yMax] = yScale.domain();

        // Prefer the "round" tick values from the un-zoomed view (multiples of
        // 100 / 200), but fall back to d3's auto-ticking when zoomed in far
        // enough that those round ticks become sparse.
        const xStep = 100;
        const xTickStart = Math.ceil(xDomainMin / xStep) * xStep;
        const baseXTicks = Array.from(
          new Set([xDomainMin, ...d3.range(xTickStart, xDomainMax + 1, xStep)]),
        ).filter((v) => v >= xMin && v <= xMax);
        const yStep = 200;
        const yTickStart = Math.ceil(yDomainMin / yStep) * yStep;
        const baseYTicks = Array.from(
          new Set([yDomainMin, ...d3.range(yTickStart, yDomainMax + 1, yStep)]),
        ).filter((v) => v >= yMin && v <= yMax);

        const xTickValues = baseXTicks.length >= 4 ? baseXTicks : xScale.ticks(8);
        const yTickValues = baseYTicks.length >= 4 ? baseYTicks : yScale.ticks(6);

        // Gridlines
        gridYG.call(
          d3
            .axisLeft(yScale)
            .tickValues(yTickValues)
            .tickSize(-innerWidth)
            .tickFormat("")
        );
        gridYG.selectAll("line").attr("stroke", "#d9dfe6");
        gridYG.select(".domain").remove();

        gridXG.call(
          d3
            .axisBottom(xScale)
            .tickValues(xTickValues)
            .tickSize(-innerHeight)
            .tickFormat("")
        );
        gridXG.selectAll("line").attr("stroke", "#d9dfe6");
        gridXG.select(".domain").remove();

        // Axes
        axisYG.call(
          d3
            .axisLeft(yScale)
            .tickValues(yTickValues)
            .tickFormat(d3.format(".1f"))
        );
        axisXG.call(
          d3
            .axisBottom(xScale)
            .tickValues(xTickValues)
            .tickFormat(d3.format(".1f"))
        );

        // Scatter dots
        dotsG
          .selectAll("circle.dot")
          .data(dataRef.current)
          .join(
            (enter) =>
              enter
                .append("circle")
                .attr("class", "dot")
                .attr("r", DOT_R)
                .attr("fill", "#4472C4")
                .attr("fill-opacity", 0.25)
                .style("pointer-events", "none")
                .attr("cx", (d) => xScale(d.x))
                .attr("cy", (d) => yScale(d.y)),
            (update) =>
              update.attr("cx", (d) => xScale(d.x)).attr("cy", (d) => yScale(d.y))
          );

        // Regression line
        if (regressionData) {
          const regressionLine = d3
            .line()
            .x((d) => xScale(d.x))
            .y((d) => yScale(d.y));

          trendG
            .selectAll("path.regression")
            .data([regressionData])
            .join(
              (enter) =>
                enter
                  .append("path")
                  .attr("class", "regression")
                  .attr("fill", "none")
                  .attr("stroke", "red")
                  .attr("stroke-width", 2)
                  .attr("d", regressionLine),
              (update) => update.attr("d", regressionLine)
            );
        }

        // Rebuild quadtree in (zoomed) pixel space so hover snapping
        // continues to work correctly at every zoom level.
        quadtree = d3
          .quadtree()
          .x((d) => xScale(d.x))
          .y((d) => yScale(d.y))
          .addAll(dataRef.current);
      }

      // Tracks whether the user is actively click-and-drag panning the chart
      // (only possible when zoomed in past the default 1x view).
      let isDragging = false;
      // After a real drag-pan, the trailing `click` event (if the browser
      // fires one) must not be treated as a click-to-select.
      let suppressNextClick = false;
      // Mouse hover state for the current draw cycle. The persistent
      // keyboard/click selection lives on `selectedRef` (component scope).
      let hoveredDatum = null;
      let hoveredEvent = null;

      const cursorForZoom = (k) => (k > 1 ? "grab" : "crosshair");

      const hideTooltip = () => {
        if (onTooltipRef.current) {
          onTooltipRef.current((t) => ({ ...t, visible: false }));
        } else {
          tooltip?.style("opacity", 0);
        }
      };

      const hideIndicator = () => {
        crosshairV.style("opacity", 0);
        crosshairH.style("opacity", 0);
        activeDot.style("opacity", 0);
      };

      const tooltipContent = (d) => (
        <>
          <strong>Solar Output:</strong> {format(d.x)} MWdc
          <br />
          <strong>Irradiance:</strong> {format(d.y)} W/m²
        </>
      );

      const tooltipHtml = (d) =>
        `<strong>Solar Output:</strong> ${format(d.x)} MWdc<br/>` +
        `<strong>Irradiance:</strong> ${format(d.y)} W/m²`;

      // Show indicator + mouse-following tooltip for a hovered point.
      function showHover(d, sourceEvent) {
        const cx = xScale(d.x);
        const cy = yScale(d.y);
        crosshairV.attr("x1", cx).attr("x2", cx).style("opacity", 1);
        crosshairH.attr("y1", cy).attr("y2", cy).style("opacity", 1);
        activeDot.attr("cx", cx).attr("cy", cy).style("opacity", 1);

        if (onTooltipRef.current) {
          onTooltipRef.current({
            visible: true,
            x: sourceEvent.clientX,
            y: sourceEvent.clientY - 10,
            content: tooltipContent(d),
          });
        } else {
          tooltip
            ?.style("opacity", 1)
            .html(tooltipHtml(d))
            .style("left", `${sourceEvent.pageX + 10}px`)
            .style("top", `${sourceEvent.pageY - 30}px`);
        }
      }

      // Show indicator + anchored tooltip for the persistent selection.
      // The tooltip sits above the data point (no pointer to follow), and
      // we hide everything when the selected point is outside the visible
      // zoomed area.
      function showSelected(d) {
        const cx = xScale(d.x);
        const cy = yScale(d.y);
        const inView =
          cx >= 0 && cx <= innerWidth && cy >= 0 && cy <= innerHeight;
        if (!inView) {
          hideIndicator();
          hideTooltip();
          return;
        }

        crosshairV.attr("x1", cx).attr("x2", cx).style("opacity", 1);
        crosshairH.attr("y1", cy).attr("y2", cy).style("opacity", 1);
        activeDot.attr("cx", cx).attr("cy", cy).style("opacity", 1);

        const svgEl = svgRef.current;
        if (!svgEl) return;
        const rect = svgEl.getBoundingClientRect();

        if (onTooltipRef.current) {
          onTooltipRef.current({
            visible: true,
            x: rect.left + margin.left + cx,
            y: rect.top + margin.top + cy - 12,
            content: tooltipContent(d),
          });
        } else {
          tooltip
            ?.style("opacity", 1)
            .html(tooltipHtml(d))
            .style(
              "left",
              `${rect.left + window.scrollX + margin.left + cx + 10}px`
            )
            .style(
              "top",
              `${rect.top + window.scrollY + margin.top + cy - 30}px`
            );
        }
      }

      // Decide what (if anything) to render right now. Mouse hover wins
      // when present; otherwise fall back to the persisted selection
      // when the SVG has keyboard focus.
      function refreshIndicator() {
        if (hoveredDatum && hoveredEvent) {
          showHover(hoveredDatum, hoveredEvent);
          return;
        }
        if (
          selectedRef.current &&
          svgRef.current &&
          document.activeElement === svgRef.current
        ) {
          showSelected(selectedRef.current);
          return;
        }
        hideIndicator();
        hideTooltip();
      }

      function handleHover(mx, my, sourceEvent) {
        if (!dataRef.current?.length || !quadtree) return;
        const found = quadtree.find(mx, my, SNAP_RADIUS);
        if (!found) {
          hoveredDatum = null;
          hoveredEvent = null;
          refreshIndicator();
          return;
        }
        hoveredDatum = found;
        hoveredEvent = sourceEvent;
        showHover(found, sourceEvent);
      }

      // Default selection when the chart first gains focus without one:
      // the data point nearest the visible viewport center.
      function defaultSelection() {
        if (!dataRef.current?.length) return null;
        const cx0 = innerWidth / 2;
        const cy0 = innerHeight / 2;
        let best = null;
        let bestDist = Infinity;
        for (const d of dataRef.current) {
          const dx = xScale(d.x) - cx0;
          const dy = yScale(d.y) - cy0;
          const dist = dx * dx + dy * dy;
          if (dist < bestDist) {
            bestDist = dist;
            best = d;
          }
        }
        return best;
      }

      // Spatial-nav: pick the nearest data point in the requested
      // direction relative to the current selection. The 45° cone
      // (`|dx| > |dy|` for left/right, vice-versa for up/down) keeps
      // ArrowLeft from jumping to a point that's mostly above.
      function navigate(direction) {
        if (!dataRef.current?.length) return;
        if (!selectedRef.current) {
          selectedRef.current = defaultSelection();
          refreshIndicator();
          return;
        }
        const sel = selectedRef.current;
        const fromCx = xScale(sel.x);
        const fromCy = yScale(sel.y);
        let best = null;
        let bestDist = Infinity;
        for (const d of dataRef.current) {
          if (d === sel) continue;
          const dx = xScale(d.x) - fromCx;
          const dy = yScale(d.y) - fromCy;
          let inCone = false;
          if (direction === "left") inCone = dx < 0 && Math.abs(dx) > Math.abs(dy);
          else if (direction === "right") inCone = dx > 0 && Math.abs(dx) > Math.abs(dy);
          else if (direction === "up") inCone = dy < 0 && Math.abs(dy) > Math.abs(dx);
          else if (direction === "down") inCone = dy > 0 && Math.abs(dy) > Math.abs(dx);
          if (!inCone) continue;
          const dist = dx * dx + dy * dy;
          if (dist < bestDist) {
            bestDist = dist;
            best = d;
          }
        }
        if (!best) return;
        selectedRef.current = best;

        // If the new selection would be off-screen at the current zoom,
        // pan to center it. `translateTo` takes coordinates in the
        // original (un-rescaled) viewport, so we feed it `xBase/yBase`.
        //
        // Important: this check must match the bounds `showSelected` uses
        // to decide whether to render the indicator (strict 0..innerW/H).
        // If we treated "almost in view" as in-view here, we'd skip the
        // pan and then `showSelected` would silently hide the indicator
        // for points sitting just past the edge — making it look like the
        // arrow key did nothing.
        const newCx = xScale(best.x);
        const newCy = yScale(best.y);
        const inView =
          newCx >= 0 && newCx <= innerWidth &&
          newCy >= 0 && newCy <= innerHeight;
        if (!inView) {
          overlay
            .transition()
            .duration(150)
            .call(zoom.translateTo, xBase(best.x), yBase(best.y));
          // The zoom 'zoom' handler will refresh the indicator each tick.
        } else {
          refreshIndicator();
        }
      }

      // +/- zoom. The focal point is the selected data point's current
      // pixel position, so the selection stays visually anchored under
      // the zoom (it remains under the same screen pixel before and
      // after, subject to the translateExtent clamp at MIN_ZOOM).
      function zoomBy(steps) {
        const t = transformRef.current;
        const factor = Math.pow(ZOOM_STEP, steps);
        const targetK = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, t.k * factor));
        if (targetK === t.k) return;
        if (!selectedRef.current) {
          selectedRef.current = defaultSelection();
          if (!selectedRef.current) return;
        }
        const sel = selectedRef.current;
        const px = xScale(sel.x);
        const py = yScale(sel.y);
        overlay
          .transition()
          .duration(150)
          .call(zoom.scaleBy, targetK / t.k, [px, py]);
      }

      overlay
        .on("mousemove", (event) => {
          if (isDragging) return;
          const [mx, my] = d3.pointer(event);
          handleHover(mx, my, event);
        })
        .on("mouseleave", () => {
          if (isDragging) return;
          hoveredDatum = null;
          hoveredEvent = null;
          // Falls back to the persistent selection if the SVG still has
          // keyboard focus, otherwise hides everything.
          refreshIndicator();
        })
        .on("click", (event) => {
          if (suppressNextClick) {
            suppressNextClick = false;
            return;
          }
          if (isDragging) return;
          const [mx, my] = d3.pointer(event);
          const found = quadtree?.find(mx, my, SNAP_RADIUS);
          if (found) {
            selectedRef.current = found;
          }
          // Focus the SVG so subsequent arrow / +/- keys are captured.
          svgRef.current?.focus({ preventScroll: true });
          refreshIndicator();
        });

      /* =========================
         ZOOM + PAN
         - wheel up   -> zoom in toward pointer
         - wheel down -> zoom out, clamped at the default 1x view
         - click+drag -> pan (only when zoomed in; constrained to the
                         original axis bounds via translateExtent)
         - keyboard   -> +/- via `zoomBy`, anchored on the selection
         ========================= */
      const zoom = d3
        .zoom()
        .scaleExtent([MIN_ZOOM, MAX_ZOOM])
        .extent([
          [0, 0],
          [innerWidth, innerHeight],
        ])
        .translateExtent([
          [0, 0],
          [innerWidth, innerHeight],
        ])
        // Allow wheel zoom always; allow drag-pan (mousedown) only when the
        // user is already zoomed in. Block double-click zoom and touch.
        .filter((event) => {
          if (event.type === "wheel") return true;
          if (event.type === "mousedown") {
            return transformRef.current.k > MIN_ZOOM;
          }
          return false;
        })
        .on("start", (event) => {
          // A drag-pan starts with a mousedown source event. Reset the
          // dragging flag — we'll latch it on the first drag-driven
          // "zoom" event so plain clicks (no movement) don't suppress
          // the hover tooltip.
          if (event.sourceEvent && event.sourceEvent.type === "mousedown") {
            isDragging = false;
          }
        })
        .on("zoom", (event) => {
          applyTransform(event.transform);

          const sourceEvent = event.sourceEvent;
          if (!sourceEvent) {
            // Programmatic zoom (keyboard +/- or navigate auto-pan).
            // Re-render the persistent indicator at the new pixel coords.
            refreshIndicator();
            return;
          }

          if (sourceEvent.type === "mousemove") {
            // Drag-pan: latch dragging state, swap to grabbing cursor,
            // and clear any lingering hover affordances.
            if (!isDragging) {
              isDragging = true;
              overlay.style("cursor", "grabbing");
              hoveredDatum = null;
              hoveredEvent = null;
              hideIndicator();
              hideTooltip();
            }
          } else if (sourceEvent.type === "wheel") {
            // Wheel zoom: keep cursor in sync with the new zoom level
            // and re-evaluate hover at the pointer's current location.
            overlay.style("cursor", cursorForZoom(event.transform.k));
            const [mx, my] = d3.pointer(sourceEvent, overlay.node());
            handleHover(mx, my, sourceEvent);
          }
        })
        .on("end", () => {
          if (isDragging) {
            isDragging = false;
            // A real drag happened — suppress the synthesized click that
            // browsers may fire on mouseup so click-to-select isn't
            // triggered at the end of a pan gesture.
            suppressNextClick = true;
          }
          overlay.style("cursor", cursorForZoom(transformRef.current.k));
        });

      overlay.style("cursor", cursorForZoom(transformRef.current.k));
      overlay.call(zoom);

      // Restore any prior zoom state (preserved across resizes / re-renders).
      // Setting the transform via the behavior fires the "zoom" handler,
      // which performs the initial render.
      overlay.call(zoom.transform, transformRef.current);

      // Publish this draw cycle's nav/zoom/refresh hooks so the effect-
      // scope keyboard listeners always drive the latest chart instance.
      handlersRef.current = {
        navigate,
        zoomBy,
        refresh: refreshIndicator,
        ensureDefaultSelection: () => {
          if (!selectedRef.current) {
            selectedRef.current = defaultSelection();
          }
        },
      };

      // If the chart was already focused when this draw ran (e.g. a
      // resize or data refresh while the user was navigating), re-render
      // the selection so the indicator persists across redraws.
      if (svgRef.current && document.activeElement === svgRef.current) {
        if (!selectedRef.current) {
          selectedRef.current = defaultSelection();
        }
        refreshIndicator();
      }
    }

    drawRef.current = draw;
    draw();

    const observer = new ResizeObserver(draw);
    observer.observe(containerRef.current);

    /* =========================
       KEYBOARD NAVIGATION
       - Tab to focus, click on a data point to select.
       - Arrow keys move the selection in their direction (45° cones).
       - + / = zoom in toward the selected point.
       - - / _ zoom out, keeping the selected point under the same pixel.
       Listeners live at effect scope and read from `handlersRef` so they
       always drive whichever draw cycle is currently active.
       ========================= */
    const svgEl = svgRef.current;
    const dirMap = {
      ArrowLeft: "left",
      ArrowRight: "right",
      ArrowUp: "up",
      ArrowDown: "down",
    };

    const handleKeyDown = (e) => {
      const h = handlersRef.current;
      if (!h) return;
      if (dirMap[e.key]) {
        e.preventDefault();
        h.navigate(dirMap[e.key]);
        return;
      }
      if (e.key === "+" || e.key === "=" || e.code === "NumpadAdd") {
        e.preventDefault();
        h.zoomBy(1);
        return;
      }
      if (e.key === "-" || e.key === "_" || e.code === "NumpadSubtract") {
        e.preventDefault();
        h.zoomBy(-1);
      }
    };

    const handleFocus = () => {
      const h = handlersRef.current;
      if (!h) return;
      h.ensureDefaultSelection();
      h.refresh();
    };

    const handleBlur = () => {
      const h = handlersRef.current;
      if (!h) return;
      // refreshIndicator() now sees `document.activeElement !== svgEl`
      // and hides the selection indicator + tooltip.
      h.refresh();
    };

    svgEl?.addEventListener("keydown", handleKeyDown);
    svgEl?.addEventListener("focus", handleFocus);
    svgEl?.addEventListener("blur", handleBlur);

    return () => {
      observer.disconnect();
      tooltip?.remove?.();
      svgEl?.removeEventListener("keydown", handleKeyDown);
      svgEl?.removeEventListener("focus", handleFocus);
      svgEl?.removeEventListener("blur", handleBlur);
    };
  }, [xAxisLabel, yAxisLabel, xDomainMin, xDomainMax, yDomainMin, yDomainMax]);

  return (
    <div ref={containerRef} style={{ width: "100%", minWidth: 0 }}>
      <svg
        ref={svgRef}
        tabIndex={0}
        role="img"
        aria-label={ariaLabel}
        style={{ width: "100%", height: HEIGHT }}
      />
    </div>
  );
}
  